// External Imports
import FullCalendar from "@fullcalendar/react";
import dayGrid from "@fullcalendar/daygrid";
import timeGrid from "@fullcalendar/timegrid";
import list from "@fullcalendar/list";
import interaction from "@fullcalendar/interaction";
import { useContext, useEffect, useState } from "react";
import { requestAdminAPI } from "../../api/posh-api";
import "./styles.css";
import Heading from "../UI/Heading";
import { MdAdd, MdRefresh } from "react-icons/md";
import FilterMenu from "../UI/FilterMenu";
import { useLocation } from "react-router-dom";
import DateFilter from "../UI/DateFilter";
import DomainSwitch from "../UI/DomainSwitch";
import ExportCsvButton from "../UI/ExportCsvButton";
import AddEventModal from "./AddEventModal";
import SearchBar from "../UI/SearchBar";
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";
import { UtilityContext } from "../../context/util-context";
import EventModal from "./EventModal";
// Internal Imports

/** ========================================================================
 * * Calendar Component
 * This component renders a calendar.
 * ========================================================================== */

export default function Calendar() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const defaultFilters = {
    type: "",
    sd: "",
    ed: "",
    scope: "",
    query: "",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key])
  );

  // Initialize States
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState(paramFilters);
  const [add, setAdd] = useState(false);
  const [event, setEvent] = useState(false);

  /* ====================== FETCH EVENTS ====================== */
  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("get-events", filter);
      let filtered = response;
      // Apply search filter
      if (filter.query) {
        const newfilter = [];
        response.forEach((event) => {
          const string =
            `${event.title} ${event.description} ${event.customer} ${event.vehicle} ${event.customer_email} ${event.vehicleId}`.toLowerCase();
          if (string.includes(filter.query.toLowerCase()))
            newfilter.push(event);
        });
        filtered = newfilter;
      }

      // Replace URL
      let url = "/calendar?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });
      setEvents(filtered);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== FILTER OPTIONS ===================
  const filterOptions = [
    {
      label: "Type",
      options: [
        { label: "All", value: "" },
        { label: "Sales", value: "sales" },
        { label: "Maintenance", value: "maintenance" },
      ],
      selected: filter.type,
    },
  ];

  // Fetch events on load
  useEffect(() => {
    fetchEvents();
  }, [filter]);

  return (
    <div className="container">
      <AddEventModal open={add} setOpen={setAdd} onComplete={fetchEvents} />
      <EventModal
        open={!!event}
        setOpen={setEvent}
        event={event}
        onComplete={fetchEvents}
      />
      <Heading title="Calendar">
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
        <FilterMenu
          options={filterOptions}
          onSubmit={(f) => setFilter({ ...filter, ...f })}
        />
        <DomainSwitch
          on={filter.scope}
          onSwitch={(scope) => setFilter({ ...filter, scope })}
        />
        <MdAdd className="text-3xl icon" onClick={() => setAdd(true)} />
        <MdRefresh className="icon" onClick={() => fetchEvents()} />
        <ExportCsvButton array={events} filename="events" />
      </Heading>

      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />

      <div className="w-full h-[60vh] calendar">
        {loading ? (
          <LoadingSpinnerWrapper />
        ) : (
          <FullCalendar
            plugins={[dayGrid, timeGrid, list, interaction]}
            events={events}
            initialView="dayGridMonth"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
            }}
            eventClick={(info) =>
              setEvent({
                ...info.event.toPlainObject(),
                ...info.event.toPlainObject().extendedProps,
              })
            }
            height="auto"
            allDayText="All Day"
          />
        )}
      </div>
    </div>
  );
}
